import { Deserializable } from '../../../protocols/deserializable';
import { HighGuideProductSignal } from './high-guide-product-signal';
import { HighGuideSignalSnapshotSummary } from './high-guide-signal-snapshot-summary';

/**
 * The precomputed signal rollup for a single variant.
 *
 * The dashboard neither fetches nor renders these - HighGuide asks for them itself, one variant at a time, as
 * products are opened. This port exists for the websocket push that refreshes one variant's company rollup after a
 * teammate reviews it, which the iframe cannot receive on its own and the dashboard relays.
 *
 * On that push only `company` is populated: `global` and `previewRecommendations` arrive `null` and mean "unchanged",
 * not "empty". Whoever applies it must take the company side and leave the rest of the held snapshot alone.
 *
 * Nothing here may substitute a value for one the backend did not send. A relay that turns a `null` into an empty
 * list has manufactured an instruction - "there are no previews" - out of "this push says nothing about previews",
 * and the receiver has no way left to tell the two apart.
 */
export class HighGuideProductSignalSnapshot implements Deserializable {
  public variantId: string;
  public bpdId: string;

  /** A backend-chosen sample of the underlying signals. `null` on the websocket push, meaning "unchanged". */
  public previewRecommendations: HighGuideProductSignal[];

  /** This company's rollup. `null` when nobody at this company has left a signal for the variant. */
  public company: HighGuideSignalSnapshotSummary;

  /** The network wide rollup. `null` until enough signals from enough distinct contributors exist. */
  public global: HighGuideSignalSnapshotSummary;

  onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    this.previewRecommendations = Deserialize?.arrayOf(HighGuideProductSignal, this.previewRecommendations);
    this.company = Deserialize?.instanceOf(HighGuideSignalSnapshotSummary, this.company);
    this.global = Deserialize?.instanceOf(HighGuideSignalSnapshotSummary, this.global);
  }
}
